import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Bits";
import { money, date } from "@/lib/format";
import { toast } from "sonner";
import CustomerPicker from "@/components/CustomerPicker";
import LineItems from "@/components/LineItems";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowRightCircle, Check, Printer, Mail, Trash2 } from "lucide-react";
import { SuperDelete } from "@/components/SuperDelete";
import TaskPicker from "@/components/TaskPicker";

const BILLING = { fixed: "Fixed Price", hourly: "Hourly", per_unit: "Per Unit/Part", mixed: "Mixed" };
const STATUS = {
  draft: "bg-slate-700 text-slate-200 border-slate-600",
  sent: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  accepted: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  rejected: "bg-red-500/20 text-red-300 border-red-500/50",
  converted: "bg-primary/20 text-primary border-primary/40",
};

export default function Quotations() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState({ job_types: [], stage_templates: [], staff: [], locations: [] });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank());
  const [lines, setLines] = useState([]);
  const [emailFor, setEmailFor] = useState(null);
  const [emailAddr, setEmailAddr] = useState("");
  const [sending, setSending] = useState(false);

  function blank() {
    return { customer_id: "", job_name: "", customer_email: "", customer_po: "", job_type: "", description: "", quantity: 1, due_date: "", location: "Rathcairn", quoted_value: 0, estimated_cost: 0, billing_method: "fixed", task_names: [], customer_supplied: false };
  }

  const load = useCallback(() => { api.get("/quotations").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); api.get("/config").then((r) => setCfg(r.data)); }, [load]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const lineAmt = (l) => Number(l.qty || 0) * Number(l.rate || 0);
  const linesTotal = lines.reduce((s, l) => s + lineAmt(l), 0);
  const [manualQV, setManualQV] = useState(false);
  const quotedValue = lines.length && !manualQV ? linesTotal : Number(f.quoted_value || 0);

  const create = async () => {
    try {
      const billing_lines = lines.map((l) => ({ description: l.description, qty: Number(l.qty || 0), rate: Number(l.rate || 0), amount: lineAmt(l) }));
      const payload = { ...f, billing_lines, quoted_value: quotedValue };
      payload.stages = (payload.task_names || []).map((name) => ({ name }));
      delete payload.task_names;
      await api.post("/quotations", payload);
      toast.success("Quotation created");
      setOpen(false); setF(blank()); setLines([]); setManualQV(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/quotations/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const convert = async (q) => {
    try {
      const { data } = await api.post(`/quotations/${q.id}/convert`);
      toast.success(`Converted to ${data.job_number}`);
      nav(`/jobs/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openEmail = (q) => { setEmailFor(q); setEmailAddr(q.customer_email || ""); };
  const sendEmail = async () => {
    if (!emailAddr) return toast.error("Enter the customer email");
    setSending(true);
    try {
      await api.patch(`/quotations/${emailFor.id}`, { customer_email: emailAddr });
      const { data } = await api.post(`/quotations/${emailFor.id}/send-email`);
      toast.success(`Quote emailed to ${data.to}`);
      setEmailFor(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Quotations" subtitle="Quote the work with priced line items, email it for sign-off, then convert it into a job">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-quote-button" className="font-bold uppercase"><Plus className="w-4 h-4 mr-1" />New Quote</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading uppercase">Create Quotation</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Customer *"><CustomerPicker value={f.customer_id} onChange={(c) => setF((s) => ({ ...s, customer_id: c.id, customer_email: s.customer_email || c.email || "" }))} testid="quote-customer" /></Field>
              <Field label="Customer Email"><Input type="email" value={f.customer_email} onChange={(e) => set("customer_email")(e.target.value)} data-testid="quote-email" placeholder="customer@company.ie" /></Field>
              <Field label="Job Name" full><Input value={f.job_name} onChange={(e) => set("job_name")(e.target.value)} placeholder="e.g. Steel columns – Block B" data-testid="quote-job-name" /></Field>
              <Field label="Description" full><Textarea value={f.description} onChange={(e) => set("description")(e.target.value)} rows={2} data-testid="quote-desc" /></Field>
              <Field label="Job Type">
                <Select value={f.job_type} onValueChange={set("job_type")}><SelectTrigger data-testid="quote-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{cfg.job_types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent></Select>
              </Field>
              <Field label="Quantity"><Input type="number" value={f.quantity} onChange={(e) => set("quantity")(e.target.value)} data-testid="quote-qty" /></Field>
              <Field label="Location">
                <Select value={f.location} onValueChange={set("location")}><SelectTrigger data-testid="quote-location"><SelectValue /></SelectTrigger>
                  <SelectContent>{cfg.locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select>
              </Field>
              <Field label="Due Date"><Input type="date" value={f.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="quote-due" /></Field>
              <Field label="Billing Method">
                <Select value={f.billing_method} onValueChange={set("billing_method")}><SelectTrigger data-testid="quote-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(BILLING).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </Field>
              <Field label="Tasks (carried to job) *" full>
                <TaskPicker list={cfg.task_list || []} value={f.task_names || []} onChange={set("task_names")} testid="quote-tasks" />
              </Field>
              <Field label="Est. Cost (€)"><Input type="number" value={f.estimated_cost} onChange={(e) => set("estimated_cost")(e.target.value)} data-testid="quote-estcost" /></Field>
            </div>

            <div className="mt-2">
              <LineItems lines={lines} onChange={setLines} quoted={lines.length && !manualQV ? Math.round(linesTotal * 100) / 100 : f.quoted_value}
                onQuoted={(v) => { setManualQV(true); set("quoted_value")(v); }} testid="quote-items" />
              <div className="text-sm text-right mt-2">Quote total: <span className="font-heading text-xl font-extrabold" data-testid="quote-total">{money(quotedValue)}</span></div>
            </div>

            <DialogFooter><Button onClick={create} disabled={!f.customer_id} data-testid="quote-create-submit" className="font-bold uppercase">Create Quote</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {rows.length === 0 ? <Empty text="No quotations yet. Create one, email it for sign-off, then convert it to a job when accepted." /> : (
        <div className="card-industrial overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Quote No.</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Status</th>
                  <th className="text-right px-4 py-3">Value</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.quote_number}`} className="hover:bg-accent/20 transition-colors">
                    <td className="px-4 py-3 font-mono-num text-primary font-bold whitespace-nowrap">{q.quote_number}</td>
                    <td className="px-4 py-3"><span className="font-semibold">{q.customer_name}</span>
                      <div className="text-xs text-muted-foreground truncate max-w-[240px]">{q.job_name || q.description}</div>
                      {q.sent_at && <div className="text-[10px] text-sky-400 mt-0.5">✉ emailed {date(q.sent_at)} → {q.sent_to}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border font-mono uppercase ${STATUS[q.status]}`}>{q.status}</span>
                      {q.job_id && <div className="text-[10px] text-muted-foreground mt-0.5">→ job</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num">{money(q.quoted_value)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {q.status === "converted" ? (
                        <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => nav(`/jobs/${q.job_id}`)} data-testid={`quote-view-job-${q.quote_number}`}>View Job</Button>
                        <SuperDelete entity="quote" id={q.id} label={q.quote_number} onDone={() => window.location.reload()} />
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <SuperDelete entity="quote" id={q.id} label={q.quote_number} onDone={() => window.location.reload()} />
                          <button onClick={() => openEmail(q)} data-testid={`quote-email-${q.quote_number}`} title="Email to customer"
                            className="inline-flex items-center h-8 px-2 rounded-md border border-border text-muted-foreground hover:text-sky-400 hover:border-sky-400/50 transition-colors"><Mail className="w-4 h-4" /></button>
                          <a href={`${API}/quotations/${q.id}/print`} target="_blank" rel="noreferrer" data-testid={`quote-print-${q.quote_number}`} title="Branded PDF"
                            className="inline-flex items-center h-8 px-2 rounded-md border border-border text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"><Printer className="w-4 h-4" /></a>
                          {q.status !== "accepted" && q.status !== "rejected" && (
                            <Button size="sm" variant="outline" onClick={() => setStatus(q.id, "accepted")} data-testid={`quote-accept-${q.quote_number}`}><Check className="w-4 h-4 mr-1" />Accept</Button>
                          )}
                          <Button size="sm" onClick={() => convert(q)} data-testid={`quote-convert-${q.quote_number}`} className="font-bold uppercase"><ArrowRightCircle className="w-4 h-4 mr-1" />Convert</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!emailFor} onOpenChange={(o) => !o && setEmailFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading uppercase">Email Quotation {emailFor?.quote_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Send the branded quotation to the customer for sign-off. The send is logged on the quote.</p>
          <Label className="text-xs">Customer email</Label>
          <Input type="email" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} data-testid="quote-email-input" placeholder="customer@company.ie" />
          <DialogFooter><Button onClick={sendEmail} disabled={sending || !emailAddr} data-testid="quote-email-send" className="font-bold uppercase"><Mail className="w-4 h-4 mr-1" />{sending ? "Sending…" : "Send Quote"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
